import React, { useState } from 'react';
import {
  RiDownload2Line,
  RiFileTextLine,
  RiTranslate2,
  RiLoader4Line,
  RiCheckLine,
  RiErrorWarningLine,
} from 'react-icons/ri';
import { DocumentWithStatus } from '@/types/document';
import Popover from '@/components/ui/Popover';
import { CasesApi } from '@/api';

interface DocumentDownloadMenuProps {
  caseId: string;
  document: DocumentWithStatus;
  onDownloadOriginal?: (document: DocumentWithStatus) => void;
  className?: string;
}

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

const DocumentDownloadMenu: React.FC<DocumentDownloadMenuProps> = ({
  caseId,
  document,
  onDownloadOriginal,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<{
    extractedText: DownloadStatus;
    translation: DownloadStatus;
  }>({
    extractedText: 'idle',
    translation: 'idle',
  });
  const [error, setError] = useState<string | null>(null);

  const hasExtractedText = document.extractedText && document.extractedText.length > 0;

  const handleDownloadOriginal = () => {
    if (onDownloadOriginal) {
      onDownloadOriginal(document);
    }
    setIsOpen(false);
  };

  const handleDownloadExtractedText = async (format: 'txt' | 'json' = 'txt') => {
    setDownloadStatus(prev => ({ ...prev, extractedText: 'loading' }));
    setError(null);

    try {
      const response = await CasesApi.downloadExtractedText(caseId, document.id, format);

      // Create blob and download
      const blob = new Blob([response], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const fileName = (document.customFileName || document.fileName || 'document').replace(/\.[^.]+$/, '');
      a.download = `${fileName}_extracted.${format}`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setDownloadStatus(prev => ({ ...prev, extractedText: 'success' }));
      setTimeout(() => {
        setDownloadStatus(prev => ({ ...prev, extractedText: 'idle' }));
        setIsOpen(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to download extracted text:', err);
      setError('Failed to download extracted text');
      setDownloadStatus(prev => ({ ...prev, extractedText: 'error' }));
      setTimeout(() => {
        setDownloadStatus(prev => ({ ...prev, extractedText: 'idle' }));
      }, 2000);
    }
  };

  const handleDownloadTranslation = async (format: 'txt' | 'md' = 'md') => {
    setDownloadStatus(prev => ({ ...prev, translation: 'loading' }));
    setError(null);

    try {
      const response = await CasesApi.downloadTranslatedText(caseId, document.id, format);

      // Create blob and download
      const blob = new Blob([response], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const fileName = (document.customFileName || document.fileName || 'document').replace(/\.[^.]+$/, '');
      a.download = `${fileName}_english.${format}`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setDownloadStatus(prev => ({ ...prev, translation: 'success' }));
      setTimeout(() => {
        setDownloadStatus(prev => ({ ...prev, translation: 'idle' }));
        setIsOpen(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to download translation:', err);
      setError('Failed to generate translation. Please try again.');
      setDownloadStatus(prev => ({ ...prev, translation: 'error' }));
      setTimeout(() => {
        setDownloadStatus(prev => ({ ...prev, translation: 'idle' }));
      }, 2000);
    }
  };

  const getStatusIcon = (status: DownloadStatus) => {
    switch (status) {
      case 'loading':
        return <RiLoader4Line className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <RiCheckLine className="w-4 h-4 text-green-500" />;
      case 'error':
        return <RiErrorWarningLine className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const menuContent = (
    <div className="w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
      <div className="px-3 py-2 border-b border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Download Options
        </p>
      </div>

      {/* Download Original */}
      <button
        onClick={handleDownloadOriginal}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors"
      >
        <RiDownload2Line className="w-4 h-4 text-gray-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Original Document</p>
          <p className="text-xs text-gray-500 truncate">Download the original file</p>
        </div>
      </button>

      {/* Download Extracted Text */}
      <button
        onClick={() => handleDownloadExtractedText('txt')}
        disabled={!hasExtractedText || downloadStatus.extractedText === 'loading'}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
          hasExtractedText && downloadStatus.extractedText !== 'loading'
            ? 'hover:bg-gray-50'
            : 'opacity-50 cursor-not-allowed'
        }`}
      >
        <RiFileTextLine className="w-4 h-4 text-gray-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Extracted Text</p>
          <p className="text-xs text-gray-500">
            {hasExtractedText ? 'Download as .txt file' : 'No text extracted yet'}
          </p>
        </div>
        {getStatusIcon(downloadStatus.extractedText)}
      </button>

      {/* Download with AI Translation */}
      <button
        onClick={() => handleDownloadTranslation('md')}
        disabled={!hasExtractedText || downloadStatus.translation === 'loading'}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-t border-gray-100 ${
          hasExtractedText && downloadStatus.translation !== 'loading'
            ? 'hover:bg-blue-50'
            : 'opacity-50 cursor-not-allowed'
        }`}
      >
        <RiTranslate2 className="w-4 h-4 text-blue-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">English Translation</p>
          <p className="text-xs text-gray-500">
            {hasExtractedText ? 'AI-translated to English (.md)' : 'Requires extracted text'}
          </p>
        </div>
        {getStatusIcon(downloadStatus.translation)}
      </button>

      {error && (
        <div className="px-3 py-2 border-t border-gray-100">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      content={menuContent}
      side="bottom"
      align="end"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-600 hover:text-primary-700 transition-colors ${className}`}
        aria-label="Download options"
      >
        <RiDownload2Line className="w-4 h-4" />
      </button>
    </Popover>
  );
};

export default DocumentDownloadMenu;
